import { AnimatePresence, motion } from "framer-motion";
import { Download, RefreshCw, WifiOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { levels } from "../content/levels";
import { AudioManager } from "../systems/audio";
import { tr } from "../systems/i18n";
import { applyUpdate, initializePwa, promptInstall, subscribePwa, type PwaState } from "../systems/pwa";
import { applyRunResult, hydrateFromIndexedDb, loadSave, persistSave, resetSave, storageAvailable } from "../systems/save";
import type { ChallengeMode, Difficulty, GameSettings, RunResult, SaveData } from "../types/game";
import { AchievementsScreen } from "./screens/AchievementsScreen";
import { BestiaryScreen } from "./screens/BestiaryScreen";
import { CampaignScreen } from "./screens/CampaignScreen";
import { CreditsScreen } from "./screens/CreditsScreen";
import { GameScreen } from "./screens/GameScreen";
import { HeroScreen } from "./screens/HeroScreen";
import { MainMenu } from "./screens/MainMenu";
import { SettingsScreen } from "./screens/SettingsScreen";
import { UpgradesScreen } from "./screens/UpgradesScreen";
import { LandscapeGate } from "./LandscapeGate";
import { Crest, GameButton, Panel } from "./ui";

export type ScreenName = "menu" | "campaign" | "heroes" | "upgrades" | "achievements" | "bestiary" | "settings" | "credits" | "game";
export interface BattleSelection { levelId: string; difficulty: Difficulty; mode: ChallengeMode }

export function App() {
  const [save, setSave] = useState<SaveData>(() => loadSave());
  const [screen, setScreen] = useState<ScreenName>(() => new URLSearchParams(location.search).get("screen") === "campaign" ? "campaign" : "menu");
  const [battle, setBattle] = useState<BattleSelection | null>(null);
  const [entered, setEntered] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [pwa, setPwa] = useState<PwaState>({ canInstall: false, offlineReady: false, updateReady: false });
  const [notice, setNotice] = useState<string | null>(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  const audioRef = useRef<AudioManager | null>(null);

  if (!audioRef.current) audioRef.current = new AudioManager(save.settings);
  const audio = audioRef.current;
  const language = save.settings.language;

  useEffect(() => {
    const images = ["/assets/hero-keyart-mobile.webp", "/assets/campaign-map-mobile.webp"].map((source) => {
      const image = new Image(); image.src = source; return image.decode().catch(() => undefined);
    });
    void Promise.all(images).then(() => setAssetsReady(true));
    const fallback = window.setTimeout(() => setAssetsReady(true), 1800);
    return () => window.clearTimeout(fallback);
  }, []);

  useEffect(() => {
    initializePwa();
    return subscribePwa((state) => {
      setPwa(state);
      if (state.offlineReady) setNotice(tr(language, "offlineReady"));
    });
  }, [language]);

  useEffect(() => { void hydrateFromIndexedDb(save).then((hydrated) => { if (hydrated.revision > save.revision) setSave(hydrated); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => audio.applySettings(save.settings), [audio, save.settings]);
  useEffect(() => () => audio.dispose(), [audio]);

  const updateSave = useCallback((mutator: (current: SaveData) => SaveData) => {
    setSave((current) => mutator(current));
  }, []);

  const navigate = useCallback((next: ScreenName) => {
    audio.play("click");
    setScreen(next);
    if (next !== "game") setBattle(null);
    const url = new URL(location.href);
    if (next === "campaign") url.searchParams.set("screen", "campaign"); else url.searchParams.delete("screen");
    history.replaceState({}, "", url);
  }, [audio]);

  const enterGame = async () => {
    await audio.unlock();
    audio.play("click");
    setEntered(true);
    const query = new URLSearchParams(location.search);
    if (query.get("continue") === "1" && save.stats.wins > 0) setScreen("campaign");
  };

  const startBattle = (selection: BattleSelection) => { setBattle(selection); setScreen("game"); };
  const finishBattle = (result: RunResult) => { setSave((current) => applyRunResult(current, result)); };

  const changeSettings = (settings: GameSettings) => updateSave((current) => persistSave({ ...current, settings }));
  const selectHero = (heroId: string) => updateSave((current) => persistSave({ ...current, selectedHero: heroId }));
  const flagTutorial = (flag: string) => updateSave((current) => current.tutorialFlags.includes(flag) ? current : persistSave({ ...current, tutorialFlags: [...current.tutorialFlags, flag] }));

  const doNewCampaign = () => {
    const fresh = resetSave(save.settings);
    setSave(fresh);
    setConfirmNew(false);
    setScreen("campaign");
  };

  const screenNode = useMemo(() => {
    const common = { save, language, onBack: () => navigate("campaign") };
    switch (screen) {
      case "menu": return <MainMenu save={save} language={language} onContinue={() => navigate("campaign")} onNew={() => save.stats.wins > 0 ? setConfirmNew(true) : doNewCampaign()} onNavigate={navigate} canInstall={pwa.canInstall} onInstall={() => void promptInstall()} />;
      case "campaign": return <CampaignScreen {...common} onMenu={() => navigate("menu")} onPlay={startBattle} onNavigate={navigate} />;
      case "heroes": return <HeroScreen {...common} onSelect={selectHero} />;
      case "upgrades": return <UpgradesScreen {...common} onSave={setSave} />;
      case "achievements": return <AchievementsScreen {...common} />;
      case "bestiary": return <BestiaryScreen {...common} />;
      case "settings": return <SettingsScreen {...common} onSettings={changeSettings} onSave={setSave} canInstall={pwa.canInstall} onInstall={() => void promptInstall()} />;
      case "credits": return <CreditsScreen {...common} onBack={() => navigate("menu")} />;
      case "game": {
        const level = levels.find((entry) => entry.id === battle?.levelId);
        if (!level || !battle) return <CampaignScreen {...common} onMenu={() => navigate("menu")} onPlay={startBattle} onNavigate={navigate} />;
        return <GameScreen key={`${level.id}-${battle.difficulty}-${battle.mode}`} level={level} difficulty={battle.difficulty} mode={battle.mode} save={save} audio={audio} language={language} onFinish={finishBattle} onExit={() => navigate("campaign")} onNext={(levelId) => startBattle({ levelId, difficulty: battle.difficulty, mode: "campaign" })} onTutorialFlag={flagTutorial} />;
      }
    }
  }, [screen, save, language, navigate, pwa.canInstall, battle, audio]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!assetsReady) return <div className="boot-screen"><div className="boot-screen__art" /><div className="boot-screen__shade" /><Crest size="lg" /><div className="wordmark"><b>AERA</b><i>LUME</i><span>RIFTWARDENS</span></div><div className="loading-rune"><i /></div><p>{tr(language, "loading")}</p></div>;
  if (!entered) return <button type="button" className="entry-screen" onClick={() => void enterGame()}><div className="entry-screen__art" /><div className="entry-screen__shade" /><div className="entry-screen__content"><Crest size="lg" /><div className="wordmark wordmark--hero"><b>AERA</b><i>LUME</i><span>RIFTWARDENS</span></div><p>The last beacon still remembers the dawn.</p><span className="tap-prompt">{tr(language, "tapToBegin")}</span></div></button>;

  return <div className={`app language-${language}`} style={{ "--ui-scale": save.settings.uiScale } as React.CSSProperties}>
    <AnimatePresence mode="wait"><motion.div key={screen} className="screen-motion" initial={save.settings.reducedMotion ? false : { opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} exit={save.settings.reducedMotion ? undefined : { opacity: 0, scale: 1.01 }} transition={{ duration: 0.22 }}>{screenNode}</motion.div></AnimatePresence>
    <LandscapeGate language={language} />
    {!storageAvailable() && <div className="storage-warning"><WifiOff size={16} />{tr(language, "noStorage")}</div>}
    {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
    {pwa.updateReady && <div className="update-toast"><RefreshCw size={18} /><span>{tr(language, "updateReady")}</span><GameButton variant="teal" onClick={() => void applyUpdate()}>{tr(language, "updateNow")}</GameButton></div>}
    {pwa.canInstall && screen === "menu" && !installDismissed && <Panel className="install-card"><Download size={26} /><div><b>{tr(language, "installTitle")}</b><p>{tr(language, "installBody")}</p></div><GameButton variant="teal" onClick={() => void promptInstall()}>{tr(language, "install")}</GameButton><button className="install-card__close" onClick={() => setInstallDismissed(true)}>×</button></Panel>}
    {confirmNew && <div className="modal-backdrop"><Panel className="confirm-dialog" title={tr(language, "newCampaign")}><p>{tr(language, "newGameWarning")}</p><div className="dialog-actions"><GameButton variant="ghost" onClick={() => setConfirmNew(false)}>{tr(language, "cancel")}</GameButton><GameButton variant="danger" onClick={doNewCampaign}>{tr(language, "startNew")}</GameButton></div></Panel></div>}
  </div>;
}
